import type { ChatResponsePayload } from '@/core/services/chatService';
import type { FpQaHandoff } from '@/shared/utils/fpQaHandoff';
import type {
  FpEngagementInput,
  GenerationMode,
  StartGenerationOptions,
} from '@/shared/components/navigator-results/generation/navigator-generation.types';
import { summaryBulletsFromClassification } from '../../components/fpQaCompletion';
import type { ChatMessage } from '../types/navigator-chat.types';

// Verbatim from docs/prototypes/generate_plan_chat_message.md.
export const GENERATE_CARE_GUIDE_MESSAGE =
  'The next step is to generate your comprehensive care guide. This guide will include the bQuest verified provider that can best serve your needs and curated resources to ensure you are prepared to move forward with confidence and clarity.';

export const careSeekerGreeting: ChatMessage = {
  id: '1',
  role: 'assistant',
  content:
    "Hello! I'm your bQuest Navigator. I'm here to help you find the right care and build a personalized plan. To get started, tell me a little about what's going on: who needs care, and what's prompting your search right now.",
};

export const fpGreeting: ChatMessage = {
  id: '1',
  role: 'assistant',
  content:
    "Hello! I'm your bQuest Navigator. I'll help you build a care plan for your client. To get started, tell me a little about what's going on: who needs care, and what's prompting your search right now.",
};

export type CompletionContext = {
  sessionId: string;
  careStage: string;
  summaryBullets: string[];
};

export type CompletionAction =
  | { type: 'concierge' }
  | { type: 'continue' }
  | { type: 'generate'; context: CompletionContext; startOptions: StartGenerationOptions };

/**
 * Pure completion routing. `classification_ready` is an in-progress signal
 * (the backend maps it to IN_PROGRESS), so concierge only triggers on a
 * terminal status — bailing on `classification_ready` would redirect the user
 * mid-conversation, right before the AI delivers its final step.
 */
export const resolveCompletionAction = (
  responseData: ChatResponsePayload,
  targetSessionId: string,
  fpHandoff: FpQaHandoff | null,
): CompletionAction => {
  const isTerminal =
    responseData.status === 'completed' || responseData.status === 'concierge_routed';
  const notPossible = responseData.classification?.is_possible === false;
  if (responseData.status === 'concierge_routed' || (notPossible && isTerminal)) {
    return { type: 'concierge' };
  }
  // Classified as not-possible but not terminal yet — keep the chat going.
  if (notPossible || !targetSessionId) {
    return { type: 'continue' };
  }

  const careStage = responseData.classification?.care_stage ?? '';
  const summaryBullets = summaryBulletsFromClassification(responseData);
  return {
    type: 'generate',
    context: { sessionId: targetSessionId, careStage, summaryBullets },
    startOptions: {
      mode: fpHandoff ? 'fp' : 'care-seeker',
      ...(fpHandoff
        ? {
            fpEngagement: {
              careStageName: careStage,
              summaryBullets,
              clientUserId: fpHandoff.clientUserId,
              name: fpHandoff.name,
            },
          }
        : {}),
    },
  };
};

const GENERATING_SNAPSHOT_KEY = 'bquest_nav_generating_snapshot';

export type GeneratingSnapshot = {
  sessionId: string;
  careStage: string;
  summaryBullets: string[];
  mode: GenerationMode;
  fpEngagement?: FpEngagementInput;
};

/**
 * Durable (sessionStorage) fallback for /care-navigator/generating.
 * `location.state` carries this on the happy path (button click), but a
 * reload or a direct hit on the loading page URL drops router state — and for
 * FP completions nothing else persists the session/mode (the legacy
 * `bquest_nav_session_id`/`bquest_nav_care_stage` keys are care-seeker only).
 * Saved once at chat completion, alongside `startGeneration`.
 */
export function saveGeneratingSnapshot(snapshot: GeneratingSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GENERATING_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota) — the
    // loading page still works from router state; only the reload fallback
    // is lost.
  }
}

export function readGeneratingSnapshot(): GeneratingSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(GENERATING_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeneratingSnapshot>;
    if (!parsed?.sessionId || !parsed.mode) return null;
    return {
      sessionId: parsed.sessionId,
      careStage: parsed.careStage ?? '',
      summaryBullets: parsed.summaryBullets ?? [],
      mode: parsed.mode,
      fpEngagement: parsed.fpEngagement,
    };
  } catch {
    return null;
  }
}
